import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Loader2, Search, Info, FileText, Trash2, BookOpen, Eye, EyeOff, ChevronRight, Copy } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { PdfViewerModal } from '@/components/evidence/PdfViewerModal'
import type { LibraryItem } from '@/data/mock'

interface LibraryListProps {
  items: LibraryItem[]
  supersededItems?: LibraryItem[]
  onOpenDetails?: (id: string) => void
  onDeleteItem?: (id: string) => void
  onToggleRag?: (id: string, isActive: boolean) => void
  sourceIdsWithClaims?: Set<string>
}

const DEDUP_VIA_LABEL: Record<NonNullable<LibraryItem['supersededVia']>, string> = {
  hash: 'Same file',
  doi: 'Same DOI',
  pmid: 'Same PMID',
  text_fingerprint: 'Same content',
}

type LibraryBadge = { label: string; className: string }

function getLibraryBadge(item: LibraryItem): LibraryBadge {
  if (item.status === 'failed') {
    return { label: 'Failed', className: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]' }
  }
  if (item.isActiveInRag) {
    return { label: 'Active', className: 'bg-[var(--status-verified-bg)] text-[var(--status-verified-text)]' }
  }
  return { label: 'Excluded', className: 'bg-muted text-muted-foreground' }
}

export function LibraryList({ items, supersededItems, onOpenDetails, onDeleteItem, onToggleRag, sourceIdsWithClaims }: LibraryListProps) {
  const [search, setSearch] = useState('')
  const [pdfSourceId, setPdfSourceId] = useState<string | undefined>(undefined)
  const [pdfOpen, setPdfOpen] = useState(false)
  const [duplicatesOpen, setDuplicatesOpen] = useState(false)
  const dupCount = supersededItems?.length ?? 0

  const filtered = items.filter((item) => {
    const q = search.toLowerCase()
    return (
      (item.shortCitation ?? '').toLowerCase().includes(q) ||
      item.amaCitation.toLowerCase().includes(q) ||
      item.filename.toLowerCase().includes(q) ||
      item.title.toLowerCase().includes(q)
    )
  })

  return (
    <>
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-2 pb-2 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search library..."
            className="w-full h-8 rounded-md border border-border bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {/* List */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="bg-muted/40 p-2 flex flex-col gap-2">
          {[...filtered].sort((a, b) => {
            if (a.isActiveInRag === b.isActiveInRag) return 0
            return a.isActiveInRag ? -1 : 1
          }).map((item) => {
            const badge = getLibraryBadge(item)
            const isProcessing = item.status === 'processing'
            const hasClaims = sourceIdsWithClaims?.has(item.id) ?? false

            return (
              <div
                key={item.id}
                className={`group bg-card border rounded-lg px-3 py-2.5 transition-all duration-150 border-border/40 hover:border-border ${
                  !item.isActiveInRag && !isProcessing ? 'opacity-55' : ''
                }`}
              >
                {/* Header: status badge (left) + actions + delete (right) */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isProcessing ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-muted text-muted-foreground">
                            <Loader2 className="h-2.5 w-2.5 animate-spin" strokeWidth={1.5} />
                            Processing
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>AI is refining document details...</TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-[10px] ${badge.className}`}>
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-6 w-6 transition-opacity ${
                              hasClaims
                                ? 'text-muted-foreground opacity-50'
                                : 'text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 focus-within:opacity-100'
                            }`}
                            disabled={hasClaims}
                            onClick={() => {
                              if (window.confirm('Are you sure you want to remove this document from the workspace? This will not delete the file from the system, but it will no longer appear in this project.')) {
                                onDeleteItem?.(item.id)
                              }
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px]">
                        {hasClaims
                          ? 'Cannot remove: This document is supporting active claims. Use the "Include as Source" toggle to exclude it from the AI instead.'
                          : 'Remove from workspace'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground disabled:opacity-40"
                            disabled={isProcessing}
                            onClick={() => {
                              setPdfSourceId(item.id)
                              setPdfOpen(true)
                            }}
                          >
                            <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isProcessing ? 'PDF will be available once processing completes' : 'Open PDF'}
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                          onClick={() => onOpenDetails?.(item.id)}
                        >
                          <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Details</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`h-6 w-6 ${
                            item.isActiveInRag
                              ? 'text-muted-foreground hover:text-foreground'
                              : 'text-muted-foreground/70 hover:text-foreground'
                          }`}
                          onClick={() => onToggleRag?.(item.id, !item.isActiveInRag)}
                        >
                          {item.isActiveInRag
                            ? <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                            : <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {item.isActiveInRag ? 'Exclude from AI sources' : 'Include in AI sources'}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {/* Title — reserves 2 lines so every card has the same height */}
                <p className="text-xs font-medium text-foreground leading-[1.4] line-clamp-2 min-h-[2lh]">
                  <ReactMarkdown components={{ p: ({ children }) => <>{children}</> }}>
                    {(item.shortCitationOverride ?? item.shortCitation) || item.title}
                  </ReactMarkdown>
                </p>
              </div>
            )
          })}
          {filtered.length === 0 && items.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 px-3 text-center">
              <BookOpen className="h-8 w-8 text-muted-foreground/40" strokeWidth={1.5} />
              <p className="text-xs text-muted-foreground font-medium">Your library is empty</p>
              <p className="text-xs text-muted-foreground/70">
                Upload a reference document to get started.
              </p>
            </div>
          )}
          {filtered.length === 0 && items.length > 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No items match your search</p>
          )}
          {dupCount > 0 && (
            <div className="mt-2 border-t border-border/40 pt-2">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-[11px] text-muted-foreground hover:bg-muted/60 transition-colors"
                onClick={() => setDuplicatesOpen((v) => !v)}
              >
                <span className="flex items-center gap-1.5">
                  <ChevronRight
                    className={`h-3 w-3 transition-transform ${duplicatesOpen ? 'rotate-90' : ''}`}
                    strokeWidth={1.5}
                  />
                  <Copy className="h-3 w-3" strokeWidth={1.5} />
                  <span className="font-medium">Duplicates ({dupCount})</span>
                </span>
              </button>
              {duplicatesOpen && (
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {(supersededItems ?? []).map((dup) => {
                    const viaLabel = dup.supersededVia ? DEDUP_VIA_LABEL[dup.supersededVia] : 'Duplicate'
                    return (
                      <div
                        key={dup.id}
                        className="bg-muted/30 border border-border/40 rounded-md px-3 py-2 opacity-70"
                      >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-[10px] bg-muted text-muted-foreground">
                            {viaLabel}
                          </span>
                          {dup.supersededBy && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                  onClick={() => onOpenDetails?.(dup.supersededBy!)}
                                >
                                  <Info className="h-3 w-3" strokeWidth={1.5} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Open canonical source</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
                          {dup.filename}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
    <PdfViewerModal
      open={pdfOpen}
      onOpenChange={setPdfOpen}
      documentId={pdfSourceId}
      boxes={[]}
    />
    </>
  )
}
